import Head from 'next/head'
import Link from 'next/link'
import { useState } from 'react'
import { useEffectOnce } from 'react-use'

import BrandsOvalBackground from '@/components/backgrounds/BrandsOval'
import AppliancesIcon from '@/components/icons/Appliances'
import AudioIcon from '@/components/icons/Audio'
import VideoIcon from '@/components/icons/Video'
import WirelessIcon from '@/components/icons/Wireless'
import Layout from '@/components/Layout'
import Popup from '@/components/Popup'
import DataTreeSvg from '@/public/img/Data-Tree.svg'
import CradlepointBrand from '@/public/img/mobile/brands/Cradlepoint.png'
import DishBrand from '@/public/img/mobile/brands/Dish.png'
import KingBrand from '@/public/img/mobile/brands/King.png'
import PinnacleBrand from '@/public/img/mobile/brands/Pinnacle.png'
import RingBrand from '@/public/img/mobile/brands/Ring.png'
import SurecallBrand from '@/public/img/mobile/brands/Surecall.png'
import TravlFiBrand from '@/public/img/mobile/brands/TravlFi.png'
import WinegardBrand from '@/public/img/mobile/brands/Winegard.png'
import CustomerServiceImg from '@/public/img/mobile/Customer-Service.jpg'
import MarketingDisplayImg from '@/public/img/mobile/Marketing-Pieces-4.png'
import HeaderImg from '@/public/img/mobile/Mobile-Header-Image.jpg'
import RvSvg from '@/public/img/mobile/RV.svg'
import RVDealerImg from '@/public/img/mobile/RV-Dealers-Section.png'
import SwooshSvg from '@/public/img/Swoosh.svg'
import DisplayImg from '@/public/img/travlfi/Display.png'


export default function Mobile(): JSX.Element {
	const [isPopupOpen, setIsPopupOpen] = useState(false)

	useEffectOnce(() => {
		setTimeout(() => {
			setIsPopupOpen(true)
		}, 7000)
	})

	return (
		<div className="overflow-x-hidden">
			<Head>
				<title key="page-title">Mobile Solutions | Pace International</title>
			</Head>

			{isPopupOpen && (
				<Popup close={() => setIsPopupOpen(false)}>
					<div className="relative py-4 bg-white rounded-md shadow-card text-center max-w-sm sm:py-5 sm:max-w-md xl:max-w-lg xl:rounded-lg xl:pt-6 xl:pb-8">
						<button className="h-6 w-6 z-10 mt-2 mr-2 absolute top-0 right-0 border border-gray-300 text-gray-800 rounded-full px-1 py-1 xl:h-7 xl:w-7 sm:mt-3 sm:mr-3" onClick={() => setIsPopupOpen(false)}>
							<svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>

						<p className="px-4 text-lg font-medium sm:text-xl xl:text-3xl">DISH Outdoors Dealers</p>

						<div className="w-full bg-gray-200 mt-2 p-2  sm:mt-3 xl:py-3">
							<p className="font-bold text-xl leading-tight sm:text-3xl xl:text-4xl">FREE Marketing Support Available!</p>
						</div>

						<img className="mt-2 px-2 w-full max-w-sm mx-auto sm:mt-3 xl:max-w-md" src={MarketingDisplayImg} alt="" />

						<p className="px-4 mt-2 text-sm sm:text-base sm:mt-3 xl:text-lg">High-end POP displays, marketing collateral, training materials, swag, and more.</p>

						<a href="https://paceintl.info/membership-area" className="inline-block text-sm mt-3 py-1.5 px-3 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 sm:text-base sm:px-4 sm:mt-6 xl:text-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Access Dealer Portal</a>
					</div>
				</Popup>
			)}

			<div className="relative">
				<img className="absolute bottom-0 max-w-none -ml-4 -mr-24 -mb-16 sm:-mb-20 sm:-mr-32 md:-mr-36 md:-mb-24 lg:-mb-20 lg:-mr-44 xl:-mb-16 xl:-mr-52 2xl:-mr-64 2xl:-mb-24" src={SwooshSvg} alt="" />

				<section className="relative container mx-auto pt-6 px-4 md:pt-9 sm:px-6 xl:pt-12 xl:px-8">
					<h1 className="font-extrabold text-green-400 md:text-lg lg:text-xl xl:text-2xl">Mobile Market</h1>

					<div className="mt-4 md:flex md:mt-6 xl:mt-9">
						<div className="md:w-1/2">
							<h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl xl:text-6xl">Providing travel technology</h2>
							<p className="mt-3 lg:text-lg xl:text-xl">Even the most ventured RVers in the most remote territories want to remain connected to the larger world. Welcome to a world of technology solutions offered by Pace.</p>
						</div>

						<div className="md:w-1/2 md:ml-12">
							<img className="mt-12 mx-auto rounded-lg shadow-lg md:rounded-xl md:mt-0 xl:rounded-2xl" src={HeaderImg} alt="Header" />
						</div>
					</div>
				</section>
			</div>

			<div className="container mx-auto px-4 mt-24 md:mt-28 sm:px-6 xl:px-8">
				<section>
					<div className="text-center">
						<h3 className="text-xl font-extrabold sm:text-2xl lg:text-3xl xl:text-4xl">What we do for the RV industry</h3>
						<p className="text-sm mt-3 sm:text-base md:mx-auto md:max-w-2xl lg:text-lg lg:max-w-4xl xl:max-w-5xl xl:text-xl">As the leading technology provider for mobile businesses in audio, video, wireless, and appliances, we accommodate a variety of product lines for both hardware and content delivery.</p>
					</div>

					<div className="mt-10 md:flex md:items-start xl:mt-12">
						<div className="flex items-start md:w-1/2">
							<div className="w-1/2 flex flex-col items-center justify-center">
								<AudioIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20"/>
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">Audio</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Full speaker systems to custom audio cable sizes.</p>
								</div>
							</div>

							<div className="w-1/2 flex flex-col items-center justify-center ml-9 sm:ml-12 md:ml-9 2xl:ml-16">
								<VideoIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20" />
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">Video</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Satellite TV for entertainment anywhere you travel.</p>
								</div>
							</div>
						</div>

						<div className="flex items-start mt-9 md:mt-0 md:w-1/2 md:ml-9 2xl:ml-16">
							<div className="w-1/2 flex flex-col items-center justify-center">
								<WirelessIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20" />
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">Wireless</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Signal boosters to help you stay connected anywhere.</p>
								</div>
							</div>

							<div className="w-1/2 flex flex-col items-center justify-center ml-9 sm:ml-12 md:ml-9 2xl:ml-16">
								<AppliancesIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20"/>
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">Appliances</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Stock your RVs with all the comforts of home.</p>
								</div>
							</div>
						</div>
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:items-center xl:mt-32">
					<div className="md:w-1/2">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Original equipment manufacturers (OEMs)</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">We didn’t become the leading tech provider for mobile business by accident. Our technicians are up to date on the latest systems and capabilities to ensure you’re never a step behind the competition. We provide audio, video, and cellular connectivity to RV OEM’s, dealers, and their customers who need to stay connected wherever they travel. We offer quality distribution, customer service, and technical support that you can depend on.</p>

						<Link href="/oem">
							<a className="inline-block text-sm py-1.5 px-3 mt-5 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 md:mt-6 lg:text-base xl:px-4 xl:py-2 xl:mt-6 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Learn More</a>
						</Link>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2 md:ml-16">
						<img className="mx-auto" src={RvSvg} alt="" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:flex-row-reverse md:items-center xl:mt-32">
					<div className="md:w-1/2 md:ml-16">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">RV dealers</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">For teams on the front lines that deal directly with end-users, Pace makes it simple to land a sale, install a device, and support travelers on the go. As the DISH® primary service provider, we’re equipped with the know-how and resources team’s need to deliver reliable, efficient RV solutions.</p>

						<a href="https://dishoutdoors.com/dealers" target="_blank" rel="noreferrer noopener" className="inline-block text-sm py-1.5 px-3 mt-5 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 md:mt-6 lg:text-base xl:px-4 xl:py-2 xl:mt-6 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Visit Dealer Page</a>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2">
						<img className="mx-auto" src={RVDealerImg} alt="RV Dealer" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:items-center xl:mt-32">
					<div className="md:w-1/2">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Call center support 7 days a week</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">We have built its reputation on unparalleled customer service and we will go the extra mile to make sure you and your customers are completely satisfied. If you have questions about shipping, technical support, programming and subscriber management, Pace is available regularly to help overcome any challenges. When you buy from Pace, you can count on more than just the right solution and the highest quality technical service for the job. Pace doesn’t only supply your business with products, we are proud to continue to support and service your business and customers by providing the answers and insights necessary for success.</p>

						<Link href="/call-center">
							<a className="inline-block text-sm py-1.5 px-3 mt-5 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 md:mt-6 lg:text-base xl:px-4 xl:py-2 xl:mt-6 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Learn More</a>
						</Link>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2 md:ml-16">
						<img className="mx-auto rounded-lg shadow-lg md:rounded-xl xl:rounded-2xl" src={CustomerServiceImg} alt="Customer Service" />
					</div>
				</section>

				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:flex-row-reverse md:items-center xl:mt-32">
					<div className="md:w-1/2 md:ml-16">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">TravlFi Pay As You Go Internet</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">A Pay As You Go Internet solution that automatically finds and connects to the best signal among major networks. Additionally, Pace provides marketing support and POP materials for various spaces.</p>

						<ul className="list-disc list-inside text-sm mt-4 space-y-2 sm:text-base xl:text-xl xl:mt-6">
							<li>Affordable equipment</li>
							<li>Huge, affordable data plans</li>
							<li>Embedded SIM technology that changes service among major networks</li>
							<li>Supported by Pace's award-winning RV Contact Center</li>
						</ul>

						<Link href="/mobile/travlfi">
							<a className="inline-block text-sm py-1.5 px-3 mt-5 bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 md:mt-6 lg:text-base xl:px-4 xl:py-2 xl:mt-6 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Learn More</a>
						</Link>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-lg md:mt-0 md:w-1/2">
						<img className="mx-auto" src={DisplayImg} alt="TravlFi Mobile Hotspot" />
					</div>
				</section>
			</div>

			<section className="relative mt-16 sm:mt-20 md:mt-24 xl:mt-32">
				<div className="absolute inset-0 transform translate-x-1/2">
					<BrandsOvalBackground className="h-full transform -translate-x-1/2" />
				</div>

				<div className="relative container mx-auto grid grid-cols-2-auto py-20 px-10 gap-y-16 gap-x-12 md:px-44 md:py-32 lg:px-40 lg:py-40 lg:gap-x-12 lg:gap-y-24 lg:grid-cols-4-auto 2xl:px-52 2xl:py-52">
					<img className="mx-auto" src={DishBrand} alt="Dish" />
					<Link href="/mobile/travlfi">
						<img className="mx-auto" src={TravlFiBrand} alt="TravlFi" />
					</Link>
					<img className="mx-auto" src={SurecallBrand} alt="SureCall" />
					<img className="mx-auto" src={WinegardBrand} alt="WineGard" />
					<img className="mx-auto" src={KingBrand} alt="King" />
					<img className="mx-auto" src={RingBrand} alt="Ring" />
					<img className="mx-auto" src={CradlepointBrand} alt="Winnebago" />
					<img className="mx-auto" src={PinnacleBrand} alt="Pinnacle" />
				</div>
			</section>


			<div className="container mx-auto px-4 mt-16 sm:mt-20 sm:px-6 md:mt-24 xl:mt-32 xl:px-8">
				<section className="xl:flex">
					<div className="hidden xl:w-max xl:block xl:h-max">
						<img src={DataTreeSvg} alt="" />
					</div>

					<div className="relative pb-16 xl:pb-0 xl:ml-24 xl:flex-1">
						<div className="absolute bottom-0 left-0 opacity-10 -ml-4 hidden sm:block md:-ml-6 xl:hidden">
							<img className="md:w-3/4 lg:w-5/6" src={DataTreeSvg} alt="" />
						</div>

						<div className="relative">
							<h3 className="text-xl font-extrabold sm:text-2xl xl:max-w-md xl:text-3xl">Pace connects all solutions for your business</h3>

							<div className="mt-9 md:flex">
								<div className="space-y-8 md:w-1/2">
									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">dish satellite tv</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Proudly offering DISH’s satellite TV programming for added value</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Hardware solutions</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Routers, splitters, extenders, and connectors that support the newest audio/video technology.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Installation Tools & supplies</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">In addition to DISH equipment, we supply all necessary audio and video installation equipment.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Custom cabling</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">With assembly and light manufacturing available, we supply fully functional cabling.</p>
									</div>
								</div>

								<div className="space-y-8 mt-8 md:mt-0 md:w-1/2 md:ml-20">
									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Professional A/V Distribution</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Cat 5e/6 or Coax, easily convert HD video signals to HDMI using Pace HDMI Matrix solutions.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Routers and lte products</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Hundreds of vendors help us supply all vital components for your connectivity needs.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Cellular boosters</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Reliable voice and 4G LTE data signal for all major North American cell networks.</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		</div>
	)
}

Mobile.Layout = Layout
